import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { SubmissionError } from "redux-form";

import { useAccountType } from "@js/apps/common/hooks";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { useGetManagedEmployerQuery } from "@js/apps/employer/hooks";
import { useUpdateJobMutation } from "@js/apps/jobs/api";
import { getInitialValues } from "@js/apps/jobs/apps/create-job/utils/form-initial-values";
import { getInitialValuesOnBehalfOfAClient } from "@js/apps/jobs/apps/create-job/utils/form-initial-values-on-behalf-of-a-client";
import { prepareEditJobSubmitFormValues } from "@js/apps/jobs/apps/create-job/utils/prepare-data";
import { useCanManageJobsOnBehalfOfClient } from "@js/apps/jobs/hooks";
import { Snackbar } from "@js/components/snackbar";
import { useNavigate } from "@js/hooks";
import { useIdParam } from "@js/hooks/use-id-param";
import type { JobFormValues } from "@js/types/jobs";
import { isAKeyOfAnObject, normalizePath } from "@js/utils";

import { mapManageJobErrors } from "../../utils/error-mapping";
import { useExistingJobData } from "../use-existing-job-data";

const fieldsToRemove = ["interview_criteria", "interview_questions"];

export const useEditJob = () => {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const jobId = useIdParam();
  const canManageJobAsCoreMember = useCanManageJobsOnBehalfOfClient();
  const isNodeStaffOrImpersonated = useIsNodeOrImpersonated();
  const { isEmployer } = useAccountType();
  const { data: employer } = useGetManagedEmployerQuery();
  const [updateJob] = useUpdateJobMutation();
  const {
    jobData: job,
    isFetching: isFetchingJob,
    refetchJob,
  } = useExistingJobData(jobId);
  const onSubmit = (values: JobFormValues) => {
    if (!jobId) {
      return;
    }

    const updateJobData = prepareEditJobSubmitFormValues({
      valuesArg: values,
      isEmployer,
      isNodeStaffOrImpersonated,
    });

    const path = new RegExp(`^/jobs/${jobId}/edit/?$`);
    if (path.test(pathname)) {
      fieldsToRemove.forEach((field) => {
        if (isAKeyOfAnObject(updateJobData, field)) {
          delete updateJobData[field];
        }
      });
    }

    return updateJob({ jobId, data: updateJobData })
      .unwrap()
      .catch((error) => {
        if (!error?.data) {
          return;
        }
        const newErrors = mapManageJobErrors(updateJobData, error.data);
        if (newErrors?._error) {
          Snackbar.error(String(newErrors?._error));
          throw new SubmissionError({});
        }
        throw new SubmissionError(newErrors);
      });
  };

  const onSubmitSuccess = () => {
    const prevPath: string | undefined = state?.prevPath;
    const isPrevPathValid =
      !!prevPath && !prevPath.startsWith(normalizePath(pathname));

    const canInviteTalent =
      (employer?.approved ?? false) || isNodeStaffOrImpersonated;
    const defaultPath = canInviteTalent
      ? `/jobs/${jobId}/invite_talent/`
      : `/jobs/${jobId}/proposals/`;
    navigate(isPrevPathValid ? prevPath : defaultPath);
  };

  const initialValues = useMemo(() => {
    if (!job) return {};

    if (canManageJobAsCoreMember) {
      return getInitialValuesOnBehalfOfAClient(job);
    }

    return getInitialValues(job);
  }, [job, canManageJobAsCoreMember]);

  const loading = !job;

  const hideUnsavedChangesWarningURLs = useMemo(() => {
    return [`/jobs/${jobId}/edit/`, `/jobs/${jobId}/edit/set_up/`];
  }, [jobId]);

  return {
    job,
    loading,
    onSubmit,
    onSubmitSuccess,
    initialValues,
    unsavedChangesWarning: true,
    hideUnsavedChangesWarningURLs,
    isFetchingJob,
    refetchJob,
  };
};
